/**
 * 舞台几何缓存（§11.4）。
 *
 * 红线 #47：动画启动函数里零 `getBoundingClientRect`。整个客户端**只有**这个文件
 * 会去问 DOM 要尺寸，而且只在 ResizeObserver 回调里问一次；FxController、发牌、
 * 收池、推池、FLIP 一律读 `layoutRef.current` 这份缓存。
 *
 * 两个出口，各自服务不同的消费者：
 *  - `layout`（React state）：给需要重新渲染的组件（座位 left/top、公共牌尺寸）
 *  - `layoutRef` + `subscribe`（命令式）：给动画层，避免为了拿坐标触发一次渲染
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import type { SeatIndex } from '@poker/shared'

import {
  resolveTableLayout,
  type Density,
  type Point,
  type StageSize,
  type TableLayout,
} from './seatGeometry.js'

export interface UseTableGeometryInput {
  /** 参与排布的座位号。调用方需在手牌进行中冻结这个集合（§11.4 布局输入口径） */
  readonly seats: readonly SeatIndex[]
  readonly heroSeat: SeatIndex | null
  /** 省略则由舞台尺寸与座位数推导 */
  readonly density?: Density
}

export interface TableGeometry {
  /** 挂到 `.stage` 元素上 */
  readonly stageRef: RefObject<HTMLDivElement | null>
  /** 渲染用；首帧测量前为 null */
  readonly layout: TableLayout | null
  /** 动画用：永远是最新值，读它不触发渲染 */
  readonly layoutRef: RefObject<TableLayout | null>
  /** 舞台左上角在视口中的位置。需要把舞台坐标换算成 client 坐标时用（例如 FLIP 跨层） */
  readonly stageOriginRef: RefObject<Point>
  /** 订阅几何更新（尺寸变化 / 座位变化）。返回取消订阅函数。 */
  readonly subscribe: (fn: (layout: TableLayout) => void) => () => void
}

export function useTableGeometry(input: UseTableGeometryInput): TableGeometry {
  const { seats, heroSeat, density } = input

  const stageRef = useRef<HTMLDivElement | null>(null)
  const layoutRef = useRef<TableLayout | null>(null)
  const stageOriginRef = useRef<Point>({ x: 0, y: 0 })
  const sizeRef = useRef<StageSize | null>(null)
  const listenersRef = useRef<Set<(layout: TableLayout) => void>>(new Set())
  const [layout, setLayout] = useState<TableLayout | null>(null)

  // 座位数组的身份每次渲染都会变（调用方一般是 .map 出来的），用内容做 key 才不会抖
  const seatsKey = useMemo(() => seats.join(','), [seats])
  const stableSeats = useMemo(
    () => (seatsKey === '' ? [] : seatsKey.split(',').map(Number)),
    [seatsKey],
  )

  const subscribe = useCallback((fn: (l: TableLayout) => void) => {
    listenersRef.current.add(fn)
    return () => {
      listenersRef.current.delete(fn)
    }
  }, [])

  /** 用当前缓存的舞台尺寸重算一次布局并广播。size 为 null 表示还没测量到。 */
  const recompute = useCallback(() => {
    const size = sizeRef.current
    if (size === null || size.width <= 0 || size.height <= 0) return
    // currentDensity：上一次算出来、并且已经写进 .app-shell[data-density] 的那一档。
    // 必须传 —— 舞台高度是在那一档的 --dock-h 下量出来的，几何要靠它还原总预算。
    // 不传的话，密度与 dock 高度会互相把对方推回去（691×634 上 412↔428 来回跳），
    // CSS 画出来的座位块尺寸会和几何假设的对不上。详见 seatGeometry.resolveDensity。
    const next = resolveTableLayout({
      seats: stableSeats,
      heroSeat,
      stage: size,
      ...(density === undefined ? {} : { density }),
      ...(layoutRef.current === null ? {} : { currentDensity: layoutRef.current.density }),
    })
    layoutRef.current = next
    setLayout(next)
    for (const fn of listenersRef.current) fn(next)
  }, [stableSeats, heroSeat, density])

  useEffect(() => {
    const el = stageRef.current
    if (el === null) return

    let frame = 0
    const measure = (): void => {
      frame = 0
      // ★ 全应用唯一允许读 DOM 几何的地方，且只在 RO 回调触发的这一帧里读一次。
      //   contentRect 已经给了尺寸，这里额外要 origin 是为了让 FLIP 能跨层换算坐标。
      const box = el.getBoundingClientRect()
      stageOriginRef.current = { x: box.left, y: box.top }
      sizeRef.current = { width: box.width, height: box.height }
      recompute()
    }

    // 节流到 rAF：iOS 键盘弹出/旋屏时 RO 会连发十几次，每次都重算会掉帧
    const schedule = (): void => {
      if (frame !== 0) return
      frame = requestAnimationFrame(measure)
    }

    if (typeof ResizeObserver === 'undefined') {
      schedule()
      return () => {
        if (frame !== 0) cancelAnimationFrame(frame)
      }
    }

    const ro = new ResizeObserver(schedule)
    ro.observe(el)
    schedule()

    return () => {
      ro.disconnect()
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [recompute])

  // 座位/密度变化时用已缓存的尺寸立即重算，不必等下一次 resize。
  // 用 useLayoutEffect 而不是 useEffect：换人数时若等到 paint 之后再算，
  // 会先画一帧用旧几何摆的座位，肉眼可见地闪一下。
  useLayoutEffect(() => {
    recompute()
  }, [recompute])

  return { stageRef, layout, layoutRef, stageOriginRef, subscribe }
}
